import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../RestAPI";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  FiAlertTriangle, FiCheckCircle, FiXCircle, FiActivity,
  FiTrendingUp, FiTrendingDown, FiRefreshCw, FiSearch,
  FiCpu, FiLayers, FiMonitor, FiClock, FiDownloadCloud
} from "react-icons/fi";
import "./ErrorTrackingDashboard.css";

const SEVERITY_COLORS = {
  critical: "#f44336",
  high: "#ff9800",
  medium: "#ffc107",
  low: "#4caf50",
  unknown: "#9e9e9e",
};

const CATEGORY_COLORS = [
  "#7c5cfc", "#2196f3", "#f44336", "#ff9800", "#4caf50",
  "#e91e63", "#00bcd4", "#ffc107", "#8bc34a", "#9c27b0",
];

const STAGE_COLORS = {
  not_yet_taken: "#9e9e9e",
  in_dev: "#2196f3",
  in_qa: "#ff9800",
  qa_fail: "#f44336",
  in_uat: "#9c27b0",
  production: "#4caf50",
};

const STAGE_LABELS = {
  not_yet_taken: "Not Yet Taken",
  in_dev: "In Dev",
  in_qa: "In QA",
  qa_fail: "QA Fail",
  in_uat: "In UAT",
  production: "Production",
};

const ErrorTrackingDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [moduleData, setModuleData] = useState([]);
  const [severityData, setSeverityData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [topRecurring, setTopRecurring] = useState([]);
  const [orgData, setOrgData] = useState([]);
  const [screenData, setScreenData] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [last7Days, setLast7Days] = useState([]);
  const [compMode, setCompMode] = useState("7"); // "7", "30", "custom"
  const [compFrom, setCompFrom] = useState("");
  const [compTo, setCompTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  const buildQuery = (extra = {}) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    Object.entries(extra).forEach(([k, v]) => params.append(k, v));
    const str = params.toString();
    return str ? `?${str}` : "";
  };

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    const q = buildQuery();
    const trendQ = buildQuery({ group_by: "hour" });
    const recurQ = buildQuery({ limit: "5" });

    try {
      const [sumRes, modRes, sevRes, catRes, trendRes, recRes, orgRes, scrRes, patRes] =
        await Promise.all([
          fetch(`${API_BASE_URL}error-tracking/dashboard/summary/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/by-module/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/by-severity/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/by-category/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/trend/${trendQ}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/top-recurring/${recurQ}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/by-organization/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/by-screen/${q}`, { headers }),
          fetch(`${API_BASE_URL}error-tracking/dashboard/patterns/${q}`, { headers }),
        ]);

      const [sumD, modD, sevD, catD, trendD, recD, orgD, scrD, patD] = await Promise.all([
        sumRes.json(), modRes.json(), sevRes.json(), catRes.json(),
        trendRes.json(), recRes.json(), orgRes.json(), scrRes.json(), patRes.json(),
      ]);

      if (sumD.status === "success") setSummary(sumD.data);
      if (modD.status === "success") setModuleData(modD.data || []);
      if (sevD.status === "success") setSeverityData(sevD.data || []);
      if (catD.status === "success") setCategoryData(catD.data || []);
      if (trendD.status === "success") setTrendData(trendD.data || []);
      if (recD.status === "success") setTopRecurring(recD.data || []);
      if (orgD.status === "success") setOrgData(orgD.data || []);
      if (scrD.status === "success") setScreenData(scrD.data || []);
      if (patD.status === "success") setPatterns(patD.data || []);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo]);

  const fetchComparison = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (compMode === "custom" && compFrom && compTo) {
        params.append("date_from", compFrom);
        params.append("date_to", compTo);
      } else {
        params.append("days", compMode);
      }
      const res = await fetch(`${API_BASE_URL}error-tracking/dashboard/last-7-days/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success") setLast7Days(data.data || []);
    } catch (err) {
      console.error("Error fetching comparison:", err);
    }
  }, [compMode, compFrom, compTo]);

  useEffect(() => {
    fetchAll();
    fetchComparison();
  }, []);

  useEffect(() => {
    fetchComparison();
  }, [compMode]);

  const handleFilter = () => fetchAll();

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/sync/`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setSyncResult({ type: "success", message: data.message });
        fetchAll();
      } else {
        setSyncResult({ type: "error", message: data.message || "Sync failed" });
      }
    } catch (err) {
      setSyncResult({ type: "error", message: "Sync request failed" });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 5000);
    }
  };

  const changeIndicator = (val) => {
    if (val > 0) return <span className="et-change et-change--up"><FiTrendingUp size={12} /> +{val}%</span>;
    if (val < 0) return <span className="et-change et-change--down"><FiTrendingDown size={12} /> {val}%</span>;
    return <span className="et-change et-change--neutral">0%</span>;
  };

  if (loading) {
    return (
      <div className="et-loading">
        <div className="et-loading__spinner" />
        <span>Loading Error Tracking...</span>
      </div>
    );
  }

  return (
    <div className="et-dash">
      {/* Header */}
      <div className="et-header">
        <div className="et-header__left">
          <h2 className="et-header__title"><FiAlertTriangle size={20} /> Error Tracking</h2>
          <span className="et-header__subtitle">API Error Monitoring & Analysis</span>
        </div>
        <div className="et-header__filters">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="et-date-input" />
          <span className="et-date-sep">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="et-date-input" />
          <button className="et-btn et-btn--primary" onClick={handleFilter}>
            <FiSearch size={13} /> Filter
          </button>
          <button className={`et-btn et-btn--ghost ${refreshing ? "et-btn--spin" : ""}`} onClick={fetchAll}>
            <FiRefreshCw size={13} />
          </button>
          <button className={`et-btn et-btn--sync ${syncing ? "et-btn--spin" : ""}`} onClick={handleSync} disabled={syncing}>
            <FiDownloadCloud size={13} /> {syncing ? "Syncing..." : "Sync"}
          </button>
        </div>
      </div>

      {/* Sync Result Banner */}
      {syncResult && (
        <div className={`et-sync-banner et-sync-banner--${syncResult.type}`}>
          {syncResult.type === "success" ? <FiCheckCircle size={14} /> : <FiXCircle size={14} />}
          <span>{syncResult.message}</span>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="et-cards">
          <div className="et-card et-card--total">
            <div className="et-card__icon"><FiActivity size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.total_errors}</span>
              <span className="et-card__label">Total Errors</span>
              {changeIndicator(summary.change_vs_previous?.total)}
            </div>
          </div>
          <div className="et-card et-card--critical">
            <div className="et-card__icon"><FiAlertTriangle size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.critical_high}</span>
              <span className="et-card__label">Critical / High</span>
              {changeIndicator(summary.change_vs_previous?.critical_high)}
            </div>
          </div>
          <div className="et-card et-card--patterns">
            <div className="et-card__icon"><FiSearch size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.pattern_count ?? 0}</span>
              <span className="et-card__label">Error Patterns</span>
            </div>
          </div>
          <div className="et-card et-card--batch">
            <div className="et-card__icon"><FiLayers size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.batch_gt1_count ?? 0}</span>
              <span className="et-card__label">Batch &gt; 1</span>
            </div>
          </div>
          <div className="et-card et-card--unresolved">
            <div className="et-card__icon"><FiXCircle size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.unresolved}</span>
              <span className="et-card__label">Unresolved</span>
            </div>
          </div>
          <div className="et-card et-card--analyzed">
            <div className="et-card__icon"><FiCpu size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.analyzed}</span>
              <span className="et-card__label">AI Analyzed</span>
            </div>
          </div>
          <div className="et-card et-card--pending">
            <div className="et-card__icon"><FiClock size={20} /></div>
            <div className="et-card__body">
              <span className="et-card__value">{summary.unanalyzed}</span>
              <span className="et-card__label">Pending Analysis</span>
            </div>
          </div>
        </div>
      )}

      {/* Stage Pipeline */}
      {summary?.stage_counts && (
        <div className="et-stage-pipeline">
          {Object.entries(summary.stage_counts).map(([stage, count]) => (
            <div key={stage} className="et-stage-pill" style={{ borderColor: STAGE_COLORS[stage] || "#9e9e9e" }}>
              <span className="et-stage-pill__dot" style={{ background: STAGE_COLORS[stage] || "#9e9e9e" }} />
              <span className="et-stage-pill__label">{STAGE_LABELS[stage] || stage}</span>
              <span className="et-stage-pill__count">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Charts Row 1: Module Breakdown */}
      <div className="et-row">
        <div className="et-panel et-panel--full">
          <h3 className="et-panel__title"><FiLayers size={15} /> Errors by Module</h3>
          {moduleData.length > 0 ? (
            <BarChart
              dataset={moduleData}
              xAxis={[{ scaleType: "band", dataKey: "module", tickLabelStyle: { fontSize: 10, angle: -30 } }]}
              series={[
                { dataKey: "critical", label: "Critical", color: SEVERITY_COLORS.critical, stack: "sev" },
                { dataKey: "high", label: "High", color: SEVERITY_COLORS.high, stack: "sev" },
                { dataKey: "medium", label: "Medium", color: SEVERITY_COLORS.medium, stack: "sev" },
                { dataKey: "low", label: "Low", color: SEVERITY_COLORS.low, stack: "sev" },
              ]}
              height={280}
              slotProps={{ legend: { labelStyle: { fontSize: 11 } } }}
            />
          ) : <div className="et-empty-chart">No module data</div>}
        </div>
      </div>

      {/* Charts Row 2: Trend */}
      <div className="et-row">
        <div className="et-panel et-panel--full">
          <h3 className="et-panel__title"><FiTrendingUp size={15} /> Error Trend (Hourly)</h3>
          {trendData.length > 0 ? (
            <BarChart
              dataset={trendData.map(t => ({
                ...t,
                period: new Date(t.period).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }),
              }))}
              xAxis={[{ scaleType: "band", dataKey: "period", tickLabelStyle: { fontSize: 9, angle: -45 } }]}
              series={[
                { dataKey: "total", label: "Total", color: "#7c5cfc" },
                { dataKey: "critical", label: "Critical", color: "#f44336" },
                { dataKey: "high", label: "High", color: "#ff9800" },
              ]}
              height={260}
              slotProps={{ legend: { labelStyle: { fontSize: 11 } } }}
            />
          ) : <div className="et-empty-chart">No trend data</div>}
        </div>
      </div>

      {/* Daily Error Comparison */}
      <div className="et-row">
        <div className="et-panel et-panel--full">
          <div className="et-panel__header">
            <h3 className="et-panel__title"><FiTrendingUp size={15} /> Daily Error Comparison</h3>
            <div className="et-comp-controls">
              <div className="et-comp-tabs">
                <button className={`et-comp-tab ${compMode === "7" ? "et-comp-tab--active" : ""}`} onClick={() => setCompMode("7")}>Last 7 Days</button>
                <button className={`et-comp-tab ${compMode === "30" ? "et-comp-tab--active" : ""}`} onClick={() => setCompMode("30")}>Last 30 Days</button>
                <button className={`et-comp-tab ${compMode === "custom" ? "et-comp-tab--active" : ""}`} onClick={() => setCompMode("custom")}>Custom</button>
              </div>
              {compMode === "custom" && (
                <div className="et-comp-dates">
                  <input type="date" className="et-date-input" value={compFrom} onChange={(e) => setCompFrom(e.target.value)} />
                  <span className="et-date-sep">to</span>
                  <input type="date" className="et-date-input" value={compTo} onChange={(e) => setCompTo(e.target.value)} />
                  <button className="et-btn et-btn--primary" onClick={fetchComparison} disabled={!compFrom || !compTo}>
                    <FiSearch size={13} /> Go
                  </button>
                </div>
              )}
            </div>
          </div>
          {last7Days.length > 0 ? (
            <BarChart
              dataset={last7Days}
              xAxis={[{ scaleType: "band", dataKey: "label", tickLabelStyle: { fontSize: 9, angle: last7Days.length > 10 ? -45 : 0 } }]}
              series={[
                { dataKey: "total", label: "Total Errors", color: "#7c5cfc" },
                { dataKey: "critical_high", label: "Critical / High", color: "#f44336" },
              ]}
              height={280}
              slotProps={{ legend: { labelStyle: { fontSize: 11 } } }}
            />
          ) : <div className="et-empty-chart">No data for selected range</div>}
        </div>
      </div>

      {/* Severity + Category */}
      <div className="et-row">
        <div className="et-panel et-panel--half">
          <h3 className="et-panel__title">Severity Distribution</h3>
          {severityData.length > 0 ? (
            <PieChart
              series={[{
                data: severityData.map((s, i) => ({
                  id: s.severity,
                  value: s.total,
                  label: s.label,
                  color: SEVERITY_COLORS[s.severity] || "#9e9e9e",
                })),
                innerRadius: 40,
                outerRadius: 90,
                paddingAngle: 2,
                cornerRadius: 4,
              }]}
              width={400}
              height={240}
              slotProps={{ legend: { direction: "column", position: { vertical: "middle", horizontal: "right" }, labelStyle: { fontSize: 11 } } }}
            />
          ) : <div className="et-empty-chart">No data</div>}
        </div>
        <div className="et-panel et-panel--half">
          <h3 className="et-panel__title">Error Categories</h3>
          {categoryData.length > 0 ? (
            <PieChart
              series={[{
                data: categoryData.map((c, i) => ({
                  id: c.category,
                  value: c.total,
                  label: c.label,
                  color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                })),
                innerRadius: 40,
                outerRadius: 90,
                paddingAngle: 2,
                cornerRadius: 4,
              }]}
              width={400}
              height={240}
              slotProps={{ legend: { direction: "column", position: { vertical: "middle", horizontal: "right" }, labelStyle: { fontSize: 11 } } }}
            />
          ) : <div className="et-empty-chart">No data</div>}
        </div>
      </div>

      {/* Top Recurring Errors */}
      <div className="et-row">
        <div className="et-panel et-panel--full">
          <h3 className="et-panel__title"><FiRefreshCw size={15} /> Top Recurring Errors</h3>
          {topRecurring.length > 0 ? (
            <div className="et-table-wrap">
              <table className="et-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>URL</th>
                    <th>Status</th>
                    <th>Module</th>
                    <th>Severity</th>
                    <th>Occurrences</th>
                    <th>Root Cause</th>
                  </tr>
                </thead>
                <tbody>
                  {topRecurring.map((e, i) => (
                    <tr key={i}>
                      <td><span className={`et-method et-method--${e.method?.toLowerCase()}`}>{e.method}</span></td>
                      <td className="et-url-cell" title={e.url}>{e.url?.length > 60 ? e.url.slice(0, 60) + "..." : e.url}</td>
                      <td><span className="et-status-badge">{e.status_code}</span></td>
                      <td>{e.module || "-"}</td>
                      <td><span className={`et-severity et-severity--${e.severity}`}>{e.severity || "unknown"}</span></td>
                      <td className="et-count">{e.total_occurrences}</td>
                      <td className="et-rootcause" title={e.root_cause}>{e.root_cause?.length > 80 ? e.root_cause.slice(0, 80) + "..." : e.root_cause || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="et-empty-chart">No recurring errors</div>}
        </div>
      </div>

      {/* Organization & Screen Errors */}
      <div className="et-row">
        <div className="et-panel et-panel--half">
          <h3 className="et-panel__title"><FiLayers size={15} /> By Organization</h3>
          {orgData.length > 0 ? (
            <div className="et-table-wrap et-table-wrap--scroll">
              <table className="et-table et-table--compact">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Total</th>
                    <th>Critical</th>
                    <th>High</th>
                    <th>Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {orgData.slice(0, 10).map((o, i) => (
                    <tr key={i}>
                      <td>{o.organization_name}</td>
                      <td className="et-count">{o.total}</td>
                      <td className="et-count" style={{ color: "#f44336" }}>{o.critical}</td>
                      <td className="et-count" style={{ color: "#ff9800" }}>{o.high}</td>
                      <td className="et-count" style={{ color: "#4caf50" }}>{o.resolved}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="et-empty-chart">No org data</div>}
        </div>
        <div className="et-panel et-panel--half">
          <h3 className="et-panel__title"><FiMonitor size={15} /> By Screen Path</h3>
          {screenData.length > 0 ? (
            <div className="et-table-wrap et-table-wrap--scroll">
              <table className="et-table et-table--compact">
                <thead>
                  <tr>
                    <th>Screen Path</th>
                    <th>Total</th>
                    <th>Critical</th>
                    <th>High</th>
                  </tr>
                </thead>
                <tbody>
                  {screenData.slice(0, 10).map((s, i) => (
                    <tr key={i}>
                      <td className="et-url-cell" title={s.screen_path}>{s.screen_path?.length > 40 ? s.screen_path.slice(0, 40) + "..." : s.screen_path}</td>
                      <td className="et-count">{s.total}</td>
                      <td className="et-count" style={{ color: "#f44336" }}>{s.critical}</td>
                      <td className="et-count" style={{ color: "#ff9800" }}>{s.high}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="et-empty-chart">No screen data</div>}
        </div>
      </div>

      {/* Error Patterns */}
      <div className="et-row">
        <div className="et-panel et-panel--full">
          <h3 className="et-panel__title"><FiSearch size={15} /> Error Patterns</h3>
          {patterns.length > 0 ? (
            <div className="et-patterns">
              {patterns.map((p, i) => (
                <div key={i} className="et-pattern-card">
                  <div className="et-pattern-card__header">
                    <span className={`et-method et-method--${p.method?.toLowerCase()}`}>{p.method}</span>
                    <span className="et-status-badge">{p.status_code}</span>
                    <span className={`et-severity et-severity--${p.severity}`}>{p.severity || "unknown"}</span>
                    <span className="et-stage-badge" style={{ background: STAGE_COLORS[p.stage] || "#9e9e9e" }}>
                      {STAGE_LABELS[p.stage] || p.stage_label || "Not Yet Taken"}
                    </span>
                    <span className="et-pattern-count">{p.occurrence_count}x</span>
                    {p.total_batches > 1 && <span className="et-batch-badge">Batch #{p.batch_number}</span>}
                  </div>
                  <div className="et-pattern-card__url" title={p.url}>{p.url?.length > 70 ? p.url.slice(0, 70) + "..." : p.url}</div>
                  {p.root_cause_short && <div className="et-pattern-card__cause">{p.root_cause_short}</div>}
                  {p.error_line && <div className="et-pattern-card__error">{p.error_line}</div>}
                  <div className="et-pattern-card__meta">
                    {p.affected_modules?.length > 0 && <span>Modules: {p.affected_modules.join(", ")}</span>}
                    {p.affected_orgs?.length > 0 && <span>Orgs: {p.affected_orgs.slice(0, 3).join(", ")}{p.affected_orgs.length > 3 ? ` +${p.affected_orgs.length - 3}` : ""}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="et-empty-chart">No patterns detected</div>}
        </div>
      </div>
    </div>
  );
};

export default ErrorTrackingDashboard;
